import React from "react";
import { useNavigate } from "react-router-dom";
import Video from "../assets/images/video.mp4";
import SchoolImage from "../assets/images/School.jpg";
import MicImage from "../assets/images/Mic.jpg";
import DelImage from "../assets/images/Del.jpeg";
import Reveal from "./ui/Reveal";
import Parallax from "./ui/Parallax";
import "./styles/About.scss";

const SECTIONS = [
  {
    eyebrow: "Where we are",
    title: "A home for sound at Manatee School for the Arts",
    body: [
      "Founded in 2010, the Del Couch Music Education Foundation has run a professional recording studio inside the Manatee School for the Arts for more than a decade, teaching students audio production and the music industry from the ground up.",
      "Every program, every hour of studio time, and every mentoring session is free to the students who walk through the door.",
    ],
    image: SchoolImage,
    alt: "Manatee School for the Arts",
  },
  {
    eyebrow: "What we do",
    title: "Empowering lives through music",
    body: [
      "We believe in the transformative power of music. Our programs give students access to state-of-the-art facilities and resources that inspire creativity and self-expression.",
      "Students compose, record, and produce in a professional-grade studio, gaining real skills for careers in the music industry — while building confidence, teamwork, and a sense of achievement.",
      "Our mentorship program connects students with seasoned professionals who guide them through every step of their musical journey, from mastering instruments to understanding the business of music.",
    ],
    image: MicImage,
    alt: "Microphone in the studio",
  },
];

const PROGRAMS = [
  {
    title: "Recording & production",
    body: "Four levels of classroom training take students from first session to finished master in a professional studio.",
  },
  {
    title: "One-to-one mentoring",
    body: "Voice, guitar, piano, brass and strings — taught by working musicians, shaped around what each student actually wants to make.",
  },
  {
    title: "Instruments to borrow",
    body: "Guitars, amps and drums donated by the community are lent out, so the cost of an instrument is never the reason a young musician stops.",
  },
  {
    title: "The business of music",
    body: "Songwriting, stage confidence and the industry know-how that school band and choir programs do not cover.",
  },
];

// Drawn from the Foundation's own account of its work.
const TRACK_RECORD = [
  "Produced the music at the Bradenton Area Regatta five years running",
  "Foundation students perform at the Riverwalk Regatta and WinterFest",
  "Produced WinterFest concerts drawing crowds of more than 20,000",
  "Del has toured with Jeff Cook, guitarist for Alabama, and The Allstar Band",
  "Recorded on Rick Derringer's smooth jazz album \"Free Ride\"",
  "Worked with actresses Jane Seymour and Audrey Landers",
];

const GET_INVOLVED = [
  {
    title: "For students",
    body: "Come and see the studio. Students can arrange a visit, sit in on a session, and talk through what they want to make.",
    cta: "Get in touch",
    to: "/Contact",
  },
  {
    title: "Donate an instrument",
    body: "Guitars, amps, drums and studio gear go straight into the hands of students who could not otherwise afford them.",
    cta: "Contact us about a donation",
    to: "/Contact",
  },
  {
    title: "Support the programs",
    body: "Gifts fund instruments, studio time and mentorship, and keep every program free for the students who need it.",
    cta: "Donate",
    to: "/Donations",
  },
];

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="about page">
      {/* Cinematic video hero */}
      <section className="about__hero">
        <video autoPlay loop muted playsInline className="about__hero-video">
          <source src={Video} type="video/mp4" />
        </video>
        <div className="about__hero-scrim" />
        <div className="about__hero-inner">
          <Reveal>
            <span className="eyebrow">Our story</span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1>
              Welcome to the <em>Del Couch</em> Music Education Foundation
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p>Empowering lives through the love of music.</p>
          </Reveal>
        </div>
      </section>

      {/* Alternating feature sections */}
      {SECTIONS.map((s, i) => (
        <section className={`about__feature ${i % 2 ? "about__feature--reverse" : ""}`} key={s.title}>
          <div className="about__feature-inner">
            <Parallax speed={36} className="about__feature-media">
              <img src={s.image} alt={s.alt} loading="lazy" />
            </Parallax>
            <div className="about__feature-text">
              <Reveal>
                <span className="eyebrow">{s.eyebrow}</span>
              </Reveal>
              <Reveal delay={0.05}>
                <h2>{s.title}</h2>
              </Reveal>
              {s.body.map((p, j) => (
                <Reveal delay={0.1 + j * 0.05} key={j}>
                  <p>{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* What students get */}
      <section className="about__programs" id="programs">
        <div className="about__programs-inner">
          <Reveal>
            <span className="eyebrow">What students get</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2>Everything here is free</h2>
          </Reveal>
          <div className="about__programs-grid">
            {PROGRAMS.map((pr, i) => (
              <Reveal delay={0.08 * i} key={pr.title} className="about__program">
                <h3>{pr.title}</h3>
                <p>{pr.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="about__founder">
        <div className="about__founder-inner">
          <Reveal x={-40} y={0} className="about__founder-media">
            <img src={DelImage} alt="Del Couch" loading="lazy" />
          </Reveal>
          <div className="about__founder-text">
            <Reveal>
              <span className="eyebrow">The founder</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2>Del Couch</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                Del began his musical journey playing the trumpet and formed an
                award-winning brass band recognized by Herb Alpert. After serving in
                the United States Air Force Band and a successful career in real
                estate, he returned to music production, earning a master's degree
                from the Berklee College of Music.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p>
                His passion for mentoring young talent led to the creation of this
                foundation — which has positively impacted countless lives. Alumni
                include American Idol finalist Sam Woolf, singer-songwriter Matt
                Walden, Carolina Opry star Colton Cason, and Christian artist Taylor
                Zebracki.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="about__honors">
                <li>Grammy Awards Educator of the Year Nominee</li>
                <li>Mentor to American Idol finalist Sam Woolf</li>
                <li>Bay News 9 &ldquo;Everyday Hero&rdquo;</li>
                <li>Community Arts Champion, Manatee&nbsp;/&nbsp;Sarasota Arts Council</li>
                <li>Bradenton Arts Special Achievement Award</li>
                <li>Partnerships with national artists and producers</li>
                <li>Built professional music studios and curricula</li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Track record */}
      <section className="about__record">
        <div className="about__record-inner">
          <Reveal>
            <span className="eyebrow">Track record</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2>What we have been part of</h2>
          </Reveal>
          <ul className="about__record-list">
            {TRACK_RECORD.map((item, i) => (
              <Reveal delay={0.05 * i} key={item}>
                <li>{item}</li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Get involved */}
      <section className="about__cta">
        <Reveal>
          <span className="eyebrow">Get involved</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2>Want to be part of our journey?</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p>Whether you are a student, a mentor, or a supporter — we welcome you.</p>
        </Reveal>

        <div className="about__paths">
          {GET_INVOLVED.map((g, i) => (
            <Reveal delay={0.08 * i} key={g.title} className="about__path">
              <h3>{g.title}</h3>
              <p>{g.body}</p>
              <button className="text-link" onClick={() => navigate(g.to)}>
                {g.cta} →
              </button>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default About;
